import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const SettingsContext = createContext({ settings: {}, refresh: () => {} });

const DEFAULTS = {
  theme_primary: "#1A362B",
  theme_secondary: "#E8DCC4",
  theme_accent: "#B85C38",
  theme_background: "#F9F6F0",
  theme_foreground: "#1A362B",
};

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
}

function luminance({ r, g, b }) {
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

function readableFg(hex) {
  try {
    return luminance(hexToRgb(hex)) > 0.5 ? "#1A362B" : "#F9F6F0";
  } catch {
    return "#F9F6F0";
  }
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({});
  const [ready, setReady] = useState(false);

  const applyTheme = (s) => {
    const root = document.documentElement;
    const primary = s.theme_primary || DEFAULTS.theme_primary;
    const secondary = s.theme_secondary || DEFAULTS.theme_secondary;
    const accent = s.theme_accent || DEFAULTS.theme_accent;
    const bg = s.theme_background || DEFAULTS.theme_background;
    const fg = s.theme_foreground || DEFAULTS.theme_foreground;
    root.style.setProperty("--primary", primary);
    root.style.setProperty("--primary-fg", readableFg(primary));
    root.style.setProperty("--secondary", secondary);
    root.style.setProperty("--accent", accent);
    root.style.setProperty("--bg", bg);
    root.style.setProperty("--fg", fg);
  };

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data || {});
      applyTheme(data || {});
    } catch {
      /* ignore */
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SettingsContext.Provider value={{ settings, refresh, applyTheme, ready }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
